/**
 * LeadsPanel — vista de resultados para marcas lead-gen, con la estética del
 * reporte de presentaciones (Sora/Manrope, azul, cards + tablas). Se alimenta del
 * endpoint /leads y trae el botón "Sincronizar leads" (pull del Google Sheet).
 *
 * Secciones (calcan el reporte): El resultado (stat strip) · Desempeño por canal
 * (tabla) · Leads acumulados · CPL semanal (barras + detalle) · Desglose por
 * plataforma · Audiencia (industrias + espacio) · Por país · Anuncios Meta (tabla
 * con la placa literal) · Últimos leads. Lo que aún no está ingerido (search
 * terms, ciudad, split por idioma) se avisa en una nota, no se inventa.
 */
import { useState } from 'react';
import brandMetricsApi from '../../api/brandMetrics';
import AdPreviewCard from './components/AdPreviewCard';
import './LeadsPanel.css';

const SOURCE_LABEL = {
  meta_ads: 'Meta', google_ads: 'Google', linkedin_ads: 'LinkedIn', direct: 'Directo',
};
const SOURCE_NOTE = { meta_ads: 'FB · IG', google_ads: 'Search', linkedin_ads: 'Ads' };
const FLAG = {
  Uruguay: '🇺🇾', Uruguai: '🇺🇾', Brasil: '🇧🇷', Brazil: '🇧🇷', Argentina: '🇦🇷',
  Paraguay: '🇵🇾', Chile: '🇨🇱', 'Estados Unidos': '🇺🇸', México: '🇲🇽', Mexico: '🇲🇽',
  España: '🇪🇸', Colombia: '🇨🇴', Perú: '🇵🇪', Peru: '🇵🇪',
};
// Objetivo de leads de WTC: 200 leads en 120 días (lineal).
const GOAL_LEADS = 200;
const GOAL_DAYS = 120;
const nf = new Intl.NumberFormat('es-UY', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('es-UY', { maximumFractionDigits: 2 });

const money = (v) => (v == null ? '—' : `$${nf.format(v)}`);
const pct = (v) => (v == null ? '—' : `${nf2.format(v * 100)}%`);
const srcLabel = (s) => SOURCE_LABEL[s] || s;

function Bars({ items, getLabel = (i) => i.label, alt = false }) {
  if (!items.length) return <div className="lp-empty sm">Sin datos</div>;
  const max = Math.max(1, ...items.map((i) => i.leads));
  const top = Math.max(...items.map((i) => i.leads));
  return (
    <div className="lp-bars">
      {items.map((i, idx) => {
        const lead = i.leads === top;
        return (
          <div className={`lp-row ${lead ? 'lead' : alt ? 'alt' : ''}`} key={idx}>
            <span className="lab">{getLabel(i)}</span>
            <span className="track"><span className="fill" style={{ width: `${(i.leads / max) * 100}%` }} /></span>
            <span className="val">{nf.format(i.leads)}</span>
          </div>
        );
      })}
    </div>
  );
}

function SyncButton({ brandId, onSynced }) {
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState(null);
  const run = async () => {
    setState('syncing');
    setMsg(null);
    try {
      const res = await brandMetricsApi.syncBrandLeads(brandId);
      if (res?.error) {
        setState('error');
        setMsg(res.error);
      } else {
        setState('done');
        setMsg(`${res.total} leads · +${res.inserted} nuevos`);
        onSynced?.();
      }
    } catch (e) {
      setState('error');
      setMsg(e?.response?.data?.error || 'No se pudo sincronizar.');
    }
  };
  return (
    <div className="lp-sync">
      <button type="button" className="lp-syncbtn" onClick={run} disabled={state === 'syncing'}>
        {state === 'syncing' ? 'Sincronizando…' : 'Sincronizar leads'}
      </button>
      {msg && <span className={`lp-syncmsg ${state === 'error' ? 'err' : ''}`}>{msg}</span>}
    </div>
  );
}

export default function LeadsPanel({ brandId, data, isLoading, isError, onSynced, previewFetcher, canSync = true }) {
  if (isLoading && !data) {
    return <p className="py-12 text-center text-sm text-gray-500">Cargando leads…</p>;
  }
  if (isError || !data) {
    return (
      <div className="lp">
        <div className="lp-head">
          <div><div className="lp-eyebrow">Resultados</div><h2 className="lp-h2">Leads</h2></div>
          {canSync && <SyncButton brandId={brandId} onSynced={onSynced} />}
        </div>
        <div className="lp-empty">Todavía no hay leads cargados. Tocá “Sincronizar leads”.</div>
      </div>
    );
  }

  const s = data.summary || {};
  const perPlatform = (data.per_platform || []).filter((p) => p.leads || p.spend);
  const cplWeekly = data.cpl_weekly || [];
  const cumulative = data.cumulative || [];
  const byCountry = data.by_country || [];
  const byActivity = data.by_activity || [];
  const bySpace = data.by_space || [];
  const breakdown = data.platform_breakdown || [];
  const ads = data.ads || [];
  const geo = data.geo || {};
  const geoCountries = geo.countries || [];
  const topCities = geo.top_cities || [];
  const searchTerms = data.search_terms || [];
  const metaLang = data.meta_language || [];

  const cMax = Math.max(1, ...cumulative.map((d) => d.cumulative));
  const hotFrom = Math.max(0, cumulative.length - 5);
  const cplMax = Math.max(1, ...cplWeekly.map((w) => w.cpl || 0));
  const chTotal = perPlatform.reduce(
    (a, p) => ({
      impr: a.impr + (p.impressions || 0), spend: a.spend + (p.spend || 0), leads: a.leads + (p.leads || 0),
    }),
    { impr: 0, spend: 0, leads: 0 }
  );
  const topChannel = perPlatform.reduce((best, p) => (p.leads > (best?.leads || 0) ? p : best), null);
  // Objetivo lineal: 200 leads en 120 días → ritmo/día × días del período = esperado.
  const periodDays = (() => {
    const st = data.period?.start;
    const en = data.period?.end;
    if (!st || !en) return null;
    return Math.max(1, Math.round((new Date(en) - new Date(st)) / 86400000) + 1);
  })();
  const expectedLeads = periodDays != null ? (GOAL_LEADS / GOAL_DAYS) * periodDays : null;
  const goalPct = expectedLeads ? (s.total_leads || 0) / expectedLeads : null;
  const adsTotal = ads.reduce(
    (a, x) => ({ spend: a.spend + (x.spend || 0), impr: a.impr + (x.impressions || 0), leads: a.leads + (x.leads || 0) }),
    { spend: 0, impr: 0, leads: 0 }
  );

  return (
    <div className="lp">
      <div className="lp-head">
        <div>
          <div className="lp-eyebrow">Resultados · {data.brand?.name}</div>
          <h2 className="lp-h2">El resultado</h2>
        </div>
        {canSync && data.sheet_backed && <SyncButton brandId={brandId} onSynced={onSynced} />}
      </div>

      {/* El resultado — stat strip */}
      <div className="lp-strip">
        <div className="lp-stat"><div className="k">Leads</div><div className="v">{nf.format(s.total_leads || 0)}</div>
          {s.qualified_leads ? <div className="lp-sub">{nf.format(s.qualified_leads)} calificados</div> : null}
        </div>
        <div className="lp-stat"><div className="k">Inversión</div><div className="v">{money(s.total_spend)}</div></div>
        <div className="lp-stat"><div className="k">CPL</div><div className="v">{money(s.blended_cpl)}</div></div>
        <div className="lp-stat">
          <div className="k">% del objetivo</div>
          <div className="v">{goalPct != null ? pct(goalPct) : '—'}</div>
          {expectedLeads != null && (
            <div className={`lp-pace ${goalPct >= 1 ? 'ok' : goalPct >= 0.7 ? 'warn' : 'bad'}`}>
              {nf.format(s.total_leads || 0)} / {nf.format(Math.round(expectedLeads))} esperados
            </div>
          )}
        </div>
      </div>

      {/* Desempeño por canal — tabla */}
      {perPlatform.length > 0 && (
        <div className="lp-panel">
          <div className="lp-ph">Desempeño por canal <span className="lp-th-note">USD</span></div>
          <table className="lp-table">
            <thead>
              <tr><th>Canal</th><th>Impresiones</th><th>Inversión</th><th>Leads</th><th>Costo/lead</th><th>Señal clave</th></tr>
            </thead>
            <tbody>
              {perPlatform.map((p) => (
                <tr key={p.source}>
                  <td>{srcLabel(p.source)} <span className="sub">{SOURCE_NOTE[p.source] || ''}</span></td>
                  <td>{nf.format(p.impressions || 0)}</td>
                  <td>{money(p.spend)}</td>
                  <td>{nf.format(p.leads)}</td>
                  <td className="ok">{money(p.cpl)}</td>
                  <td className="sig">CTR {p.ctr != null ? pct(p.ctr) : '—'}</td>
                </tr>
              ))}
              <tr className="tot">
                <td>Total</td>
                <td>{nf.format(chTotal.impr)}</td>
                <td>{money(chTotal.spend)}</td>
                <td>{nf.format(chTotal.leads)}</td>
                <td className="ok">{money(chTotal.leads ? chTotal.spend / chTotal.leads : null)}</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>
          {topChannel && (
            <div className="lp-reading">
              <b>{srcLabel(topChannel.source)}</b> es el motor de leads: {nf.format(topChannel.leads)} de los {nf.format(chTotal.leads)}, a ~{money(topChannel.cpl)} c/u.
              {' '}Costo por lead global: <b>{money(chTotal.leads ? chTotal.spend / chTotal.leads : null)}</b>.
            </div>
          )}
        </div>
      )}

      {/* Leads acumulados */}
      {cumulative.length > 0 && (
        <div className="lp-panel">
          <div className="lp-ph">Leads acumulados
            <span className="lp-th-note">{cumulative[0].date.slice(5)} → {cumulative[cumulative.length - 1].date.slice(5)}</span>
          </div>
          <div className="lp-ramp tall">
            {cumulative.map((d, i) => (
              <div className="d" key={d.date}>
                <div className="rt">{nf.format(d.cumulative)}<small> +{d.leads}</small></div>
                <div className={`bar ${i >= hotFrom ? 'hot' : 'cool'}`} style={{ height: `${(d.cumulative / cMax) * 100}%` }} />
                <div className="dl">{d.date.slice(8)}/{d.date.slice(5, 7)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Costo por lead — barras + detalle semanal */}
      {cplWeekly.length > 0 && (
        <div className="lp-grid2">
          <div className="lp-panel">
            <div className="lp-ph">Costo por lead · todas las plataformas <span className="lp-th-note">USD</span></div>
            {cplWeekly.some((w) => w.cpl != null) ? (
              <div className="lp-ramp tall">
                {cplWeekly.map((w, i) => (
                  <div className="d" key={w.week} title={`${w.week}: ${money(w.cpl)}`}>
                    <div className="dv">{w.cpl != null ? money(w.cpl) : '—'}</div>
                    <div className={`bar ${i >= cplWeekly.length - 2 ? 'hot' : 'cool'}`} style={{ height: `${w.cpl ? (w.cpl / cplMax) * 100 : 3}%` }} />
                    <div className="dl">S{i + 1}</div>
                  </div>
                ))}
              </div>
            ) : <div className="lp-empty sm">Sin inversión cargada todavía</div>}
          </div>
          <div className="lp-panel">
            <div className="lp-ph">Detalle semanal <span className="lp-th-note">USD</span></div>
            <table className="lp-table">
              <thead><tr><th>Semana</th><th>Inversión</th><th>Leads</th><th>CPL</th><th>CPL Meta</th></tr></thead>
              <tbody>
                {cplWeekly.map((w, i) => (
                  <tr key={w.week}>
                    <td>S{i + 1} <span className="sub">{w.week.slice(5)}</span></td>
                    <td>{money(w.spend)}</td>
                    <td>{nf.format(w.leads)}</td>
                    <td className="ok">{money(w.cpl)}</td>
                    <td>{money(w.meta_cpl)}</td>
                  </tr>
                ))}
                <tr className="tot">
                  <td>Total</td>
                  <td>{money(cplWeekly.reduce((a, w) => a + (w.spend || 0), 0))}</td>
                  <td>{nf.format(cplWeekly.reduce((a, w) => a + (w.leads || 0), 0))}</td>
                  <td className="ok">{money(s.blended_cpl)}</td>
                  <td>—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Desglose por plataforma */}
      {breakdown.length > 0 && (
        <div className="lp-panel">
          <div className="lp-ph">Desglose por plataforma · por campaña <span className="lp-th-note">USD</span></div>
          <div className="lp-bk">
            {breakdown.map((p) => (
              <div className="bkcard" key={p.source}>
                <div className="bh">
                  <span className="bn">{srcLabel(p.source)}</span>
                  <span className="bt">{money(p.spend)} · {nf.format(p.leads)} leads · CPL {money(p.cpl)}</span>
                </div>
                <table className="lp-table">
                  <thead><tr><th>Campaña</th><th>Inv</th><th>Impr</th><th>Clk</th></tr></thead>
                  <tbody>
                    {p.campaigns.slice(0, 5).map((c, i) => (
                      <tr key={i}>
                        <td className="lp-placa">{c.name}</td>
                        <td>{money(c.spend)}</td>
                        <td>{nf.format(c.impressions)}</td>
                        <td>{nf.format(c.clicks)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audiencia — industria + interés por espacio (solo marcas con detalle por lead) */}
      {(byActivity.length > 0 || bySpace.length > 0) && (
      <div className="lp-grid2">
        <div className="lp-panel">
          <div className="lp-ph">Principales industrias</div>
          <Bars items={byActivity} />
        </div>
        <div className="lp-panel">
          <div className="lp-ph">Interés por espacio</div>
          <Bars items={bySpace} alt />
        </div>
      </div>
      )}

      {/* Por país */}
      {byCountry.length > 0 && (
      <div className="lp-panel">
        <div className="lp-ph">Por país — de dónde vienen los leads</div>
        <div className="lp-co">
          {byCountry.map((c, i) => {
            const max = Math.max(1, ...byCountry.map((x) => x.leads));
            const top = Math.max(...byCountry.map((x) => x.leads));
            return (
              <div className={`crow ${c.leads === top ? 'lead' : ''}`} key={i}>
                <span className="cn"><span className="flag">{FLAG[c.label] || '🏳️'}</span> {c.label}</span>
                <span className="track"><span className="fill" style={{ width: `${(c.leads / max) * 100}%` }} /></span>
                <span className="val">{nf.format(c.leads)}</span>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Inversión por ciudad/región — geo (solo marcas con geo cargado, ej. WTC) */}
      {geoCountries.length > 0 && (
        <div className="lp-panel">
          <div className="lp-ph">Por país · inversión agrupada por ciudad <span className="lp-th-note">USD</span></div>
          <table className="lp-table">
            <thead>
              <tr><th>País</th><th>Inversión</th><th>% inv</th><th>Ciudades</th><th>Ciudad principal</th><th>Leads</th><th>% leads</th><th>CPL</th></tr>
            </thead>
            <tbody>
              {geoCountries.map((c, i) => (
                <tr key={i}>
                  <td><span className="flag">{FLAG[c.country] || '🏳️'}</span> {c.country}</td>
                  <td>{money(c.spend)}</td>
                  <td>{pct(c.spend_share)}</td>
                  <td>{nf.format(c.cities)}</td>
                  <td className="sub">{c.top_city}</td>
                  <td>{nf.format(c.leads)}</td>
                  <td>{pct(c.leads_share)}</td>
                  <td className="ok">{money(c.cpl)}</td>
                </tr>
              ))}
              <tr className="tot">
                <td>Total</td>
                <td>{money(geo.total_spend)}</td>
                <td>100%</td>
                <td>{nf.format(geo.n_cities || 0)}</td>
                <td>—</td>
                <td>{nf.format(s.total_leads || 0)}</td>
                <td>100%</td>
                <td className="ok">{money(s.blended_cpl)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {topCities.length > 0 && (
        <div className="lp-grid2">
          <div className="lp-panel">
            <div className="lp-ph">Top 10 ciudades · % de la inversión <span className="lp-th-note">USD</span></div>
            <div className="lp-co">
              {topCities.map((c, i) => {
                const max = Math.max(1, ...topCities.map((x) => x.spend));
                return (
                  <div className={`crow ${i === 0 ? 'lead' : ''}`} key={i}>
                    <span className="cn"><span className="flag">{FLAG[c.country] || '🏳️'}</span> {c.city}</span>
                    <span className="track"><span className="fill" style={{ width: `${(c.spend / max) * 100}%` }} /></span>
                    <span className="val">{money(c.spend)} · {pct(c.spend_share)}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="lp-panel">
            <div className="lp-ph">Concentración geográfica</div>
            <div className="lp-sig" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="s"><div className="sk">Top 10 ciudades</div><div className="sv">{pct(geo.top10_share)}</div><div className="sd">de la inversión</div></div>
              <div className="s"><div className="sk">Top 3 ciudades</div><div className="sv">{pct(geo.top3_share)}</div><div className="sd">{topCities.slice(0, 3).map((c) => c.city).join(' · ')}</div></div>
              <div className="s"><div className="sk">Ciudades con pauta</div><div className="sv">{nf.format(geo.n_cities || 0)}</div></div>
              <div className="s"><div className="sk">Países</div><div className="sv">{nf.format(geoCountries.length)}</div><div className="sd">{geoCountries.slice(0, 4).map((c) => c.country).join(' · ')}</div></div>
            </div>
          </div>
        </div>
      )}

      {/* Qué buscan en Google — search terms por idioma */}
      {searchTerms.length > 0 && (
        <div className="lp-grid2">
          {searchTerms.map((g) => (
            <div className="lp-panel" key={g.language}>
              <div className="lp-ph">
                <span className="flag">{g.language === 'Português' ? '🇧🇷' : g.language === 'Español' ? '🇪🇸' : '🌎'}</span> {g.language} · impresiones
                <span className="lp-th-note">CTR {g.ctr != null ? pct(g.ctr) : '—'}</span>
              </div>
              <table className="lp-table">
                <tbody>
                  {g.terms.map((t, i) => (
                    <tr key={i}>
                      <td className="lp-placa" title={t.term}>“{t.term}”</td>
                      <td>{nf.format(t.impressions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Base de Meta por idioma — ES / PT */}
      {metaLang.length > 0 && (
        <div className="lp-panel">
          <div className="lp-ph">Base de Meta por idioma <span className="lp-th-note">USD</span></div>
          <div className="lp-sig" style={{ gridTemplateColumns: '1fr 1fr' }}>
            {metaLang.map((b) => (
              <div className="s" key={b.language}>
                <div className="sk"><span className="flag">{b.language === 'Português' ? '🇧🇷' : '🇪🇸'}</span> {b.language}</div>
                <div className="sv">{money(b.cpl)}<small> /lead</small></div>
                <div className="sd">{nf.format(b.leads)} leads · inv. {money(b.spend)} · {nf.format(b.impressions)} impr · CTR {b.ctr != null ? pct(b.ctr) : '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Anuncios Meta — placas con mini vista previa del creativo */}
      {ads.length > 0 && (
        <div className="lp-panel">
          <div className="lp-ph">Anuncios Meta activos · placas <span className="lp-th-note">USD</span></div>
          <table className="lp-table" style={{ marginBottom: 20 }}>
            <thead>
              <tr><th>Placa</th><th>Inversión</th><th>% gasto</th><th>Impresiones</th><th>Clicks</th><th>CTR</th><th>Leads</th><th>CPL</th></tr>
            </thead>
            <tbody>
              {ads.map((a, i) => (
                <tr key={i}>
                  <td className="lp-placa" title={a.name}>{a.name}{i === 0 && <span className="lp-tag">Top gasto</span>}</td>
                  <td>{money(a.spend)}</td>
                  <td>{pct(a.spend_share)}</td>
                  <td>{nf.format(a.impressions)}</td>
                  <td>{nf.format(a.clicks)}</td>
                  <td className="sig">{a.ctr != null ? pct(a.ctr) : '—'}</td>
                  <td>{a.leads ? nf.format(a.leads) : '—'}</td>
                  <td className="ok">{money(a.cpl)}</td>
                </tr>
              ))}
              <tr className="tot">
                <td>Total</td>
                <td>{money(adsTotal.spend)}</td>
                <td>100%</td>
                <td>{nf.format(adsTotal.impr)}</td>
                <td>—</td>
                <td>—</td>
                <td>{adsTotal.leads ? nf.format(adsTotal.leads) : '—'}</td>
                <td className="ok">{money(adsTotal.leads ? adsTotal.spend / adsTotal.leads : null)}</td>
              </tr>
            </tbody>
          </table>
          <div className="lp-ads">
            {ads.map((a, i) => (
              <AdPreviewCard key={i} ad={a} fetchPreview={previewFetcher}>
                <div className="lp-adname" title={a.name}>
                  {a.name}{i === 0 && <span className="lp-tag">Top gasto</span>}
                </div>
                <div className="lp-admeta">
                  <span><b>{money(a.spend)}</b> · {pct(a.spend_share)} del gasto</span>
                  <span>{nf.format(a.impressions)} impr · {nf.format(a.clicks)} clics · CTR {a.ctr != null ? pct(a.ctr) : '—'}</span>
                </div>
              </AdPreviewCard>
            ))}
          </div>
          <div className="lp-reading">
            Meta no reporta conversión por anuncio a este nivel: los leads se leen por formulario. La placa con más gasto es <b>{ads[0]?.name}</b>.
          </div>
        </div>
      )}
    </div>
  );
}
